// Multi-tenant navigation hook that preserves organization parameter
// Add this to: evv-frontend/src/hooks/useOrganizationNavigation.js

import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';
import { safeStorage } from '../utils/safeStorage';

export const useOrganizationNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get current organization from URL params
  const currentOrg = searchParams.get('org');

  // Enhanced navigate function that preserves org parameter
  const navigateWithOrg = useCallback((to, options = {}) => {
    let targetPath = to;
    
    // If we have an organization parameter, preserve it
    if (currentOrg) {
      const separator = targetPath.includes('?') ? '&' : '?';
      targetPath = `${targetPath}${separator}org=${currentOrg}`;
    }
    
    navigate(targetPath, options);
  }, [navigate, currentOrg]);

  // Helper to get current organization slug
  const getOrganizationSlug = useCallback(() => {
    return currentOrg || safeStorage.getItem('dev_organization') || 'arasync-platform';
  }, [currentOrg]);

  // Helper to check if we're in a specific organization context
  const isOrganization = useCallback((orgSlug) => {
    return currentOrg === orgSlug;
  }, [currentOrg]);

  // Helper to build URL with organization parameter
  const buildUrlWithOrg = useCallback((path) => {
    if (currentOrg) {
      const separator = path.includes('?') ? '&' : '?';
      return `${path}${separator}org=${currentOrg}`;
    }
    return path;
  }, [currentOrg]);

  return {
    navigate: navigateWithOrg,
    location,
    currentOrg,
    getOrganizationSlug,
    isOrganization,
    buildUrlWithOrg
  };
};
